import { getDefaultBuConfig } from 'domain/repository/apiUtil';
import _ from 'lodash';
import { AutoCompleteChangeParams /*, AutoCompleteSelectParams*/ } from 'primereact/autocomplete';
import { VirtualScrollerLazyParams } from 'primereact/virtualscroller';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { DynamicProp } from 'veronica-ui-component/dist/common/commonInterface';
import { Icons } from 'veronica-ui-component/dist/common/Icon';
import { HPHSkyBlue } from 'veronica-ui-component/dist/component/core/Colors';
import { IconButton } from 'veronica-ui-component/dist/component/core/IconButtons/IconButtons';
import { Lozenges } from 'veronica-ui-component/dist/component/core/Lozenges/Lozenges';
import {
    AutoCompleteGlobalStyles, StyledAutoComplete, StyledInputErrorMsg, StyledInputLabel,
    StyledInputWrapper,
    StyledLozengesContainer,
    StyledMenuItemTemplate, StyledMenuItemTemplateIcon,
    StyledMenuItemTemplateValue,
    StyledOptional,
    StyledRequired,
} from './styled/NbisInputDropDown.styled';

// Define types for options and props
export interface DropdownOption {
    dropdownLabel?: string;
    value?: string | number;
    tagLabel?: string | number;
    icon?: string;
    isMaster?: boolean;
    lozengesLabel?: string;
    lozengesVariation?: string;
    showText?: string;
    [key: string]: any;
}

interface VirtualScrollerOptions extends DynamicProp {
    showLoader?: boolean;
    delay?: number;
    lazy?: boolean;
    onLazyLoad?(e: VirtualScrollerLazyParams): void;
}

export interface InputDropdownProps extends DynamicProp {
    id?: string;
    options?: DropdownOption[];
    freeTextInput?: boolean;
    mode?: 'single' | 'multiple';
    virtualScrollerOptions?: VirtualScrollerOptions;
    onChange?(value: DropdownOption | DropdownOption[] | null): void;
    onDropdownClick?(): void;
    inputType?: 'number' | 'text' | 'freeText';
    field?: string;
    width?: string;
    arrowBackground?: 'transparent' | 'white';
    disableInput?: boolean;
    sort?: boolean;
    scrollHeight?: string;
    isVirtualScroll?: boolean;
    label?: string;
    required?: boolean;
    optional?: boolean;
    errorMessage?: string;
    helpIcon?: boolean;
    toolTipText?: string;
    placeholder?: string;
    value?: DropdownOption | DropdownOption[] | string | null;
    isLimitOptions?: boolean;
    isCustomBlur?: boolean;
    isSelFirstWhenEmpty?: boolean
}

const NbisInputDropDown = React.forwardRef((props: InputDropdownProps, ref?: React.Ref<any>) => {
    const {
        id = 'inputDropdownId',
        options = [],
        freeTextInput = false,
        mode = 'single',
        virtualScrollerOptions = {
            delay: 0,
            lazy: true,
            showLoader: true
        },
        onChange,
        onDropdownClick,
        inputType = 'freeText',
        field = 'dropdownLabel',
        width = '250px',
        arrowBackground = 'white',
        disableInput = false,
        sort = true,
        scrollHeight,
        isVirtualScroll = true,
        label = 'Label',
        required = false,
        optional = false,
        errorMessage = '',
        helpIcon = false,
        toolTipText = 'Helper Message',
        placeholder = '',
        value: propsValue = null,
        isLimitOptions = true,
        isCustomBlur = false,
        isSelFirstWhenEmpty = false,
        ...rest
    } = props;
    const [selectedValue, setSelectedValue] = useState<any>(null);
    const [filteredList, setFilteredList] = useState<DropdownOption[]>([]);
    const [panelLeft, setPanelLeft] = useState<number | null>(null);
    const [tempValue, setTempValue] = useState<string>('');
    const elemRef = useRef<any>();
    const inputLabel = useRef<HTMLLabelElement>(null);
    const [propOptions, setPropOptions] = useState<DropdownOption[]>([]);
    const defaultDropdownLimmit: Number = getDefaultBuConfig<Number>("REACT_APP_DROPDOWN_LIMIT_COUNT") || 0;
    const [optsLimitCount] = useState<number>(defaultDropdownLimmit ? Number(defaultDropdownLimmit) : 0);
    const selectedItemChangeRef = useRef<boolean>(false);
    const shouldKeepOpenRef = useRef<boolean>(false);
    // Track whether wrappedCompleteMethod already handled closing (to skip onDropdownClickInternal logic)
    const didHandleCloseRef = useRef<boolean>(false);
    // Add a ref to store the current input value
    const currentInputValueRef = useRef<string>('');

    useImperativeHandle(ref, () => ({
        inputLabel: inputLabel,
    }));

    // Add a function to retrieve the input text value
    const getInputText = useCallback(() => {
        if (elemRef.current && elemRef.current.inputRef && elemRef.current.inputRef.current) {
            return elemRef.current.inputRef.current.value || '';
        }
        return '';
    }, []);

    const flipDropdownPanel = useCallback(() => {
        const container = elemRef.current?.container as HTMLElement | undefined;
        if (!container) return;

        const formPanel = container?.parentElement?.closest('.add-edit-form') as HTMLElement | undefined;
        const formRect = formPanel?.getBoundingClientRect();
        const dropdownRect = container.getBoundingClientRect();

        // Position the panel: set height/top and reveal it.
        // Returns true if the panel was found and positioned, false if it needs another frame.
        const positionPanel = (): boolean => {
            const panelElement = container.querySelector(".p-autocomplete-panel") as HTMLElement | null;
            if (!panelElement) return false;

            // No form context — just reveal the panel at its default position
            if (!formRect) {
                panelElement.style.visibility = 'visible';
                return true;
            }

            const items = panelElement.querySelectorAll("li");
            if (items.length === 0) return false; // items not rendered yet, retry

            let itemHeight = 30;
            if (items.length > 0) {
                itemHeight = items[0].offsetHeight + 1;
            }
            const expectedPanelHeight = Math.min(
                (items.length > 9 ? 9 : items.length) * itemHeight,
                280 // Cap at CSS max-height (225 default, 280 inside .common-field)
            );

            // dropdownRect is the container (<span class="p-autocomplete">) rect.
            // container has position:relative, so panel's absolute top is relative to container.
            // container top ≈ input top, container height ≈ input height.
            const bottomHeight = formRect.bottom - dropdownRect.bottom;
            const topHeight = dropdownRect.top - formRect.top;

            if (bottomHeight < expectedPanelHeight) {
                if (bottomHeight > topHeight) {
                    // Show below, constrain height so the panel is not clipped by the form's bottom edge
                    panelElement.style.height = `${bottomHeight}px`;
                    panelElement.style.overflowY = 'auto';
                    // Reset top in case it was set to a negative value by a previous flip
                    panelElement.style.top = `${dropdownRect.height}px`;
                } else {
                    // Show above: set panel top to -finalHeight (relative to container).
                    // Since container has position:relative and panel has position:absolute,
                    // top: -finalHeight places the panel above the container (above the input)
                    // by exactly finalHeight pixels.
                    // This avoids transform conflicts with PrimeReact's CSS transition classes.
                    const finalHeight = Math.min(expectedPanelHeight, topHeight);
                    panelElement.style.height = `${finalHeight}px`;
                    panelElement.style.overflowY = 'auto';
                    panelElement.style.top = `${-finalHeight}px`;
                }
            }

            // Reveal the panel after positioning.
            panelElement.style.visibility = 'visible';
            return true;
        };

        // Try synchronously first. PrimeReact's onShow fires after the panel is mounted,
        // so the panel and its items are usually already in the DOM.
        if (!positionPanel()) {
            // Panel or items not ready yet — poll on next animation frame.
            const retry = () => {
                if (!positionPanel()) {
                    requestAnimationFrame(retry);
                }
            };
            requestAnimationFrame(retry);
        }
    }, []);

    // Find matching options
    const findMatchingOptions = useCallback((inputText: string): DropdownOption[] => {
        if (!inputText || inputText.trim() === '') {
            return [];
        }

        const searchText = inputText.trim();
        let sortedOptions = sort ? [...options]?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!)) : [...options];

        // Use the same rating and sorting logic as searchList
        const scoredOptions = sortedOptions?.map(option => {
            const label = String(option.value || option.dropdownLabel || '');
            const lowerLabel = label.toLowerCase();

            let score = 0;
            if (lowerLabel.startsWith(searchText.toLowerCase())) {
                score += 2;
            } else if (lowerLabel.includes(searchText.toLowerCase())) {
                score += 1;
            }

            return { option, score, label };
        }).filter(item => item.score > 0).sort((a, b) => {
            return b.score - a.score ||
                a.label.localeCompare(b.label);
        });

        // Get options
        let matchedOptions = scoredOptions.map(item => item.option);

        // Apply the same quantity limit as searchList
        if (isLimitOptions && matchedOptions && matchedOptions.length > 0) {
            matchedOptions = matchedOptions.slice(0, optsLimitCount);
        }

        return matchedOptions;
    }, [isLimitOptions, options, optsLimitCount, sort]);

    const searchList = useCallback(({ query }: { query: string }) => {
        let sortedOptions = sort ? [...options]?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!)) : [...options];

        let filteredOpts: DropdownOption[] = [];
        // When query is empty, always show all options (ignore tempValue)
        if (query === '') {
            filteredOpts = [...sortedOptions];
        } else if (query !== '') {
            const searchText = query;
            // Save the current input value
            currentInputValueRef.current = searchText;

            const tmpfilteredList = sortedOptions?.map(option => {
                const label = String(option.dropdownLabel || option.value || '');
                const lowerLabel = label.toLowerCase();

                let score = 0;
                if (lowerLabel.startsWith(searchText.toLowerCase())) {
                    score += 2;
                } else if (lowerLabel.includes(searchText.toLowerCase())) {
                    score += 1;
                }

                return { option, score, label };

            }).filter(item => item.score > 0).sort((a, b) => {
                return b.score - a.score ||
                    a.label.localeCompare(b.label);
            }).map(item => ({
                ...item.option,
                showText: (item.option?.dropdownLabel || item.option?.value)?.toString().replace(new RegExp(`(${searchText})`, 'gi'), match => `<span class="highlight">${match}</span>`)
            }));
            setTempValue(searchText);
            filteredOpts = tmpfilteredList;
        }

        let limitOpts: DropdownOption[] = [];
        if (isLimitOptions && filteredOpts && filteredOpts.length > 0) {
            limitOpts = filteredOpts.slice(0, optsLimitCount);
        } else {
            limitOpts = filteredOpts;
        }
        setFilteredList(limitOpts);
    }, [sort, options, isLimitOptions, optsLimitCount]);

    // Wrap completeMethod to implement toggle behavior in multiple mode:
    // On second dropdown click, skip searchList so filteredList doesn't change,
    // preventing componentDidUpdate from calling showOverlay() (which causes blank panel flicker)
    const wrappedCompleteMethod = useCallback((e: { query: string }) => {
        // Use PrimeReact's actual overlayVisible state as source of truth
        const isOverlayVisible = !!(elemRef.current && elemRef.current.state && elemRef.current.state.overlayVisible);
        // Only toggle-close when query is empty (dropdown button click)
        // When user types in input, query is non-empty and we should search normally
        if (mode === 'multiple' && isOverlayVisible && e.query === '') {
            // Panel is already open — this is a re-click on dropdown button, skip searchList
            didHandleCloseRef.current = true;
            return;
        }
        searchList(e);
    }, [mode, searchList]);

    const isSelected = useCallback((item: DropdownOption, selectedValue: DropdownOption | DropdownOption[] | null, mode: 'single' | 'multiple') => {
        if (mode === 'multiple' && _.isArray(selectedValue)) {
            return selectedValue?.some(i => i.value === item.value);
        }
        return selectedValue && !_.isArray(selectedValue) && selectedValue?.value === item.value;
    }, []);

    const escapeHtml = useCallback((text: string) => {
        return text.replace(/[<>"&]/g, m => ({
            '<': '&lt;', '>': '&gt;',
            '&': '&amp;', '"': '&quot;'
        }[m]!));
    }, []);

    const getDisplayText = useCallback((item: DropdownOption, isItemSelected: boolean) => {
        if (!item) return { __html: '' };
        if (item.showText) {
            return { __html: item.showText };
        }
        let displayText = (item.dropdownLabel || item.value || '').toString();
        displayText = escapeHtml(displayText);
        return isItemSelected ? { __html: `<span class="highlight">${displayText}</span>` } : { __html: `${displayText}` };
    }, [escapeHtml]);

    const handleOptionMouseDown = useCallback((option: DropdownOption) => {
        selectedItemChangeRef.current = true;
    }, []);

    const itemTemplate = useCallback((item: DropdownOption) => {
        if (!item) return null;

        const isItemSelected = item ? isSelected(item, selectedValue, mode) : false;
        const displayText = getDisplayText(item, isItemSelected ?? false);

        return (
            <StyledMenuItemTemplate className={isItemSelected ? 'dropdownSelected' : ''}
                onMouseDown={(e) => {
                    handleOptionMouseDown(item);
                }}>
                {item.icon && (
                    <StyledMenuItemTemplateIcon className={isItemSelected ? 'iconSelected' : ''}>
                        {Icons(item.icon, isItemSelected ? HPHSkyBlue : undefined)}
                    </StyledMenuItemTemplateIcon>
                )}
                <StyledMenuItemTemplateValue dangerouslySetInnerHTML={displayText} />
                {item.isMaster && (
                    <StyledLozengesContainer id="LozengesContainer">
                        <Lozenges label={item.lozengesLabel} variation={item.lozengesVariation} />
                    </StyledLozengesContainer>
                )}
            </StyledMenuItemTemplate>
        );
    }, [isSelected, selectedValue, mode, getDisplayText, handleOptionMouseDown]);

    const selectedMultipleItemTemplate = useCallback((item: DropdownOption) => (
        <StyledMenuItemTemplate>
            {item.icon && <StyledMenuItemTemplateIcon className="icon">{Icons(item.icon)}</StyledMenuItemTemplateIcon>}
            <StyledMenuItemTemplateValue>{item[field || 'value'] || item.value}</StyledMenuItemTemplateValue>
        </StyledMenuItemTemplate>
    ), [field]);

    const isNumber = useCallback((evt: React.KeyboardEvent<HTMLInputElement>) => evt.which > 31 && (evt.which < 48 || evt.which > 57) ? false : true, []);

    const isAlfa = useCallback((evt: React.KeyboardEvent<HTMLInputElement>) => evt.which > 31 && (evt.which < 65 || evt.which > 90) && (evt.which < 97 || evt.which > 122) ? false : true, []);

    const handleFreeText = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (freeTextInput && e.which === 13 && tempValue) {
            const itemExists = propOptions?.find(item => item.value === tempValue || item.dropdownLabel === tempValue);
            if (!itemExists) {
                setFilteredList(prevFilteredList => [...prevFilteredList, { value: tempValue }]);
                const newValue = mode === 'multiple' ? [...(Array.isArray(selectedValue) ? selectedValue : []), { value: tempValue }] : tempValue;
                setSelectedValue(newValue as DropdownOption | DropdownOption[] | null);
                setTempValue('');
                if (mode === 'multiple') {
                    elemRef.current.inputRef.current.value = '';
                }
                onChange && onChange(newValue as DropdownOption | DropdownOption[] | null);
            }
        }
    }, [freeTextInput, tempValue, propOptions, mode, selectedValue, onChange]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        const shouldType = inputType === 'number' ? isNumber(e) : inputType === 'text' ? isAlfa(e) : true;
        shouldType ? handleFreeText(e) : e.preventDefault();
    }, [inputType, isNumber, isAlfa, handleFreeText]);

    /*const selectFirstItem = useCallback(() => {
        if (!filteredList?.length && !isSelFirstWhenEmpty) return;

        if (mode === 'single' && !freeTextInput) {
            let tempSelValue: DropdownOption | null = null;
            if (filteredList?.length) {
                tempSelValue = filteredList[0];
            } else if (isSelFirstWhenEmpty && options?.length > 0) {
                tempSelValue = options[0];
            }
            
            setSelectedValue(tempSelValue);
            onChange && onChange(tempSelValue);
            if (elemRef?.current?.inputRef?.current) {
                elemRef.current.inputRef.current.value = '';
            }            
        } else if (mode === 'multiple' && !freeTextInput) {
            if (filteredList?.length) {
                const newSelectedValue = _.isEmpty(selectedValue) ?
                    [filteredList[0]] :
                    [...selectedValue, filteredList[0]];
                setSelectedValue(newSelectedValue);
                onChange && onChange(newSelectedValue);
                if (elemRef?.current?.inputRef?.current) {
                    elemRef.current.inputRef.current.value = '';
                }
            }
        }
    }, [filteredList, isSelFirstWhenEmpty, mode, freeTextInput, onChange, options, selectedValue]);*/

    const handleBlur = useCallback(() => {
        // Use a flag to check if user just clicked on an option
        if (selectedItemChangeRef.current) {
            selectedItemChangeRef.current = false;
            return;
        }

        // Get current input text
        const inputText = getInputText();
        const hasInputText = inputText && inputText.trim() !== '';

        // Case 1: No input text
        if (!hasInputText) {
            // When isSelFirstWhenEmpty is enabled in single mode with no free text input
            if (isSelFirstWhenEmpty && mode === 'single' && !freeTextInput && options?.length > 0) {
                // Select first option (original functionality)
                // Get first option according to sort rules
                let firstOption = options[0];
                if (sort) {
                    const sortedOptions = [...options]?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!));
                    firstOption = sortedOptions[0];
                }
                setSelectedValue(firstOption);
                onChange && onChange(firstOption);
            }
            // Clear filtered list — skip for multiple mode to preserve list for next open
            if (mode !== 'multiple') {
                setFilteredList([]);
            }
            return;
        }

        // Case 2: Has input text
        // Find matching options (using same logic as search)
        const matchingOptions = findMatchingOptions(inputText);

        if (matchingOptions.length > 0) {
            // Has matching options, select the first one
            const firstMatch = matchingOptions[0];

            if (mode === 'single') {
                // Single mode: set selected value to first match
                setSelectedValue(firstMatch);
                onChange && onChange(firstMatch);
            } else if (mode === 'multiple') {
                // Multiple mode: clear input field first
                if (elemRef?.current?.inputRef?.current) {
                    elemRef.current.inputRef.current.value = '';
                }
                // Check if already selected
                const isAlreadySelected = _.some(selectedValue,
                    (item: DropdownOption) => _.isEqual(item.value, firstMatch.value));

                if (!isAlreadySelected) {
                    // Add to selected values
                    const newSelectedValue = _.isEmpty(selectedValue) ?
                        [firstMatch] :
                        [...selectedValue, firstMatch];
                    setSelectedValue(newSelectedValue);
                    onChange && onChange(newSelectedValue);
                }
            }
        } else {
            // No matching options found
            if (freeTextInput) {
                // If free text input is allowed, add as new item
                const newItem = {
                    value: inputText,
                    dropdownLabel: inputText,
                    tagLabel: inputText
                };

                if (mode === 'single') {
                    // Single mode: set to new item
                    setSelectedValue(newItem);
                    onChange && onChange(newItem);
                } else if (mode === 'multiple') {
                    // Multiple mode: add to selected values
                    const newSelectedValue = _.isEmpty(selectedValue) ?
                        [newItem] :
                        [...selectedValue, newItem];
                    setSelectedValue(newSelectedValue);
                    onChange && onChange(newSelectedValue);
                }
            } else {
                // Non-free text input mode
                // Clear input field
                if (elemRef?.current?.inputRef?.current) {
                    elemRef.current.inputRef.current.value = '';
                }
                // Clear selected value for single mode               

                if (mode === 'single') {
                    // Single mode: set to new item
                    setSelectedValue(null);
                    onChange && onChange(null);
                }
            }
        }

        // Clear temporary value
        setTempValue('');
    }, [findMatchingOptions, freeTextInput, getInputText, isSelFirstWhenEmpty, mode, onChange, options, selectedValue, sort]);

    const onDropdownClickInternal = useCallback(() => {
        // If wrappedCompleteMethod already handled closing (second click), close panel and return
        if (didHandleCloseRef.current) {
            didHandleCloseRef.current = false;
            if (elemRef.current) {
                elemRef.current.hideOverlay();
            }
            return;
        }

        const pAuto = elemRef.current.container;

        const rect = pAuto.getBoundingClientRect();

        setPanelLeft(rect.left);

        onDropdownClick && onDropdownClick();
        // Skip blur() in multiple mode — it triggers onBlur → hideOverlay → flicker
        if (elemRef.current && disableInput && mode !== 'multiple') {
            elemRef.current.inputRef.current.blur();
        }
        setTempValue('');
    }, [onDropdownClick, disableInput, mode]);

    useEffect(() => {
        let currentSelectedValue: any = propsValue;
        let sortedOptions = propOptions?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!));
        if (typeof propsValue === 'string' && propsValue.trim()) {
            const filteredList = sortedOptions?.filter(item => (item.value || item.dropdownLabel)?.toString().toLowerCase() === propsValue.toLowerCase());
            currentSelectedValue = filteredList[0] || currentSelectedValue;
        } else if (Array.isArray(propsValue)) {
            currentSelectedValue = propsValue;
        } else {
            currentSelectedValue = propsValue && typeof propsValue === 'object' ? propsValue : currentSelectedValue;
        }
        setSelectedValue(currentSelectedValue);
    }, [propOptions, propsValue, setSelectedValue]);

    useEffect(() => {
        let tempOptions: DropdownOption[] = [];
        if (isLimitOptions && options && options.length > 0) {
            tempOptions = options.slice(0, optsLimitCount);
        } else {
            tempOptions = options;
        }

        tempOptions = tempOptions?.filter(a => !(a?.dropdownLabel === null || a?.dropdownLabel === undefined || a?.dropdownLabel === ""));

        if (JSON.stringify(propOptions) !== JSON.stringify(tempOptions?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!)))) {
            setPropOptions(tempOptions);
        }
    }, [isLimitOptions, options, optsLimitCount, propOptions])

    useEffect(() => {
        if (mode === 'multiple' && elemRef.current) {
            const originalHideOverlay = elemRef.current.hideOverlay;

            elemRef.current.hideOverlay = function () {
                if (shouldKeepOpenRef.current) {
                    // Use 200ms delay to outlast PrimeReact's onBlur event
                    // which fires after selection and would close the panel
                    setTimeout(() => {
                        shouldKeepOpenRef.current = false;
                    }, 200);
                    return;
                }
                originalHideOverlay.call(this);
            };
        }
    }, [mode]);

    const handleOnChange = useCallback((e: AutoCompleteChangeParams) => {
        const stateOverlayVisible = elemRef.current && elemRef.current.state ? elemRef.current.state.overlayVisible : null;

        if (mode === 'single' && !e.value) {
            setFilteredList([]);
        }

        if (e.value && _.isArray(e.value) && !_.isEmpty(e.value) && e.value.length > selectedValue?.length) {
            const value = e.value[e.value.length - 1].value;

            if (_.some(selectedValue, (item: DropdownOption) => _.isEqual(item.value, value))) {
                return;
            }

            // Set flag to prevent hideOverlay from closing the panel
            shouldKeepOpenRef.current = true;
            // Reset filtered list to show all options
            searchList({ query: '' });

            // Scroll input to the end after selection (like onDropdownClickInternal does)
            requestAnimationFrame(() => {
                if (elemRef.current && elemRef.current.container) {
                    const multiContainer = elemRef.current.container.querySelector('.p-autocomplete-multiple-container');
                    if (multiContainer) {
                        multiContainer.scrollLeft = multiContainer.scrollWidth;
                    }
                }
            });
        } else if (mode === 'multiple' && stateOverlayVisible) {
            // Deleting a value while panel is open — keep panel open and ensure list is populated
            shouldKeepOpenRef.current = true;
            // Re-populate filteredList to prevent blank panel after re-render
            searchList({ query: '' });
        }

        setSelectedValue(e.value === "" ? null : e.value);
        setTempValue('');
        onChange && onChange(e.value === "" ? null : e.value);
    }, [mode, onChange, selectedValue, searchList]);

    /*const handleSelect = useCallback((e: AutoCompleteSelectParams) => {
        selectedItemChangeRef.current = true;
    }, []);*/

    let inputWidth, panelWidth, autoCompleteWidth = width;
    if (width && !isNaN(Number(width.replace('px', '').replace('%', '')))) {
        const w = Number(width.replace('px', '').replace('%', ''));
        panelWidth = `${w - 2}px`;
        // autoCompleteWidth = `${w - 45}px`;
        inputWidth = Array.isArray(selectedValue) && selectedValue.length ? (w * .8) : '100%';
    }

    return <>
        <AutoCompleteGlobalStyles panelLeft={panelLeft} />
        <StyledInputWrapper onMouseLeave={() => document.body.click()} className={`${(!freeTextInput && disableInput && mode === 'multiple') ? 'inputDisable' : ''}`}>
            <StyledInputLabel ref={inputLabel}>
                {label} {required && !optional && <StyledRequired>*</StyledRequired>}
                {optional && !required && <StyledOptional><Lozenges variation={'Optional Label'} /></StyledOptional>}
                {helpIcon && <IconButton fileName={'Icon-help'} size={'small'} idIcon={`Icon-help-${id}`} toolTipPlacement={'right'} toolTipArrow={false} disabled={false} toolTipText={toolTipText} {...rest} />}
            </StyledInputLabel>
            <StyledAutoComplete
                {...rest}
                arrowBackground={arrowBackground}
                autoCompleteWidth={autoCompleteWidth}
                ref={elemRef}
                autoHighlight={false}
                appendTo="self"
                panelStyle={{ visibility: 'hidden' }}
                // Disable the default 120ms enter transition so onShow fires immediately,
                // allowing flipDropdownPanel to position the panel without delay.
                transitionOptions={{ timeout: 0 }}
                errorMessage={!!errorMessage}
                {...freeTextInput ? { onKeyPress: handleKeyPress, dropdownIcon: 'Icon-auto-text' } : {}}
                onKeyPress={handleKeyPress}
                value={selectedValue}
                suggestions={filteredList}
                completeMethod={wrappedCompleteMethod}
                field={field || 'value'}
                onDropdownClick={onDropdownClickInternal}
                dropdown
                {...mode === 'multiple' ?
                    { multiple: true, selectedItemTemplate: selectedMultipleItemTemplate, removeIcon: 'pi pi-times' } :
                    {}}
                {...isVirtualScroll ? {
                    virtualScrollerOptions: {
                        ...virtualScrollerOptions,
                        ...!virtualScrollerOptions?.itemSize ? { itemSize: 30 } : {}, style: { width: panelWidth },
                    }
                } : {}}
                scrollHeight={scrollHeight === '' || scrollHeight === undefined ? (propOptions.length < 7 ? `${options.length * 30}px ` : '225px') : scrollHeight}
                style={{ width }}
                placeholder={placeholder}
                itemTemplate={itemTemplate}
                inputStyle={{ width: inputWidth }}
                readOnly={!freeTextInput && disableInput}
                onShow={() => {
                    const container = elemRef.current?.container;
                    if (container) {
                        flipDropdownPanel();
                    }
                }}
                onHide={() => {
                    // Reset inline styles applied by flipDropdownPanel so the next open
                    // starts from a clean state (panelStyle visibility:hidden reapplies via React).
                    const container = elemRef.current?.container;
                    if (container) {
                        const panel = container.querySelector(".p-autocomplete-panel") as HTMLElement | null;
                        if (panel) {
                            panel.style.visibility = '';
                            panel.style.height = '';
                            panel.style.overflowY = '';
                            panel.style.top = '';
                        }
                    }
                }}
                onChange={handleOnChange}
                {...(!isCustomBlur ? { onBlur: handleBlur } : {})}
            //onSelect={handleSelect}
            />
            <StyledInputErrorMsg>{errorMessage}</StyledInputErrorMsg>
        </StyledInputWrapper>
    </>

});

export default NbisInputDropDown;